import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { landing } from '../../content/landing';
import { selectedGuildId } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loginHref } from '../../lib/loginHref';

/**
 * Khung chung của MỌI trang public: giới thiệu, tính năng, lệnh, hướng dẫn,
 * điều khoản, bảo mật.
 *
 * Có một khung duy nhất vì thanh điều hướng là thứ dễ lệch nhất khi mỗi trang tự
 * dựng đầu trang của mình: thêm một trang mới rồi quên thêm link vào ba trang cũ
 * là trang mới tồn tại mà không ai tới được. Danh sách link nằm ở
 * `content/landing.ts`, dùng chung cho cả đầu và chân trang.
 *
 * `wide` cho trang cần khổ rộng (lưới tính năng, danh sách lệnh); mặc định là
 * khổ đọc hẹp của trang văn bản.
 */
export function PublicShell({
  children,
  wide = false,
}: {
  children: ReactNode;
  wide?: boolean;
}): React.JSX.Element {
  const location = useLocation();
  const { user, loading } = useAuth();
  const container = wide ? 'mx-auto w-full max-w-5xl px-4' : 'mx-auto w-full max-w-3xl px-4';
  /**
   * Đã chọn server thì đây là lối tắt vào PANEL của server đó, không phải về
   * lại `/` — trước 16/08/2026 nút này luôn trỏ `/`, nên đứng NGAY TRÊN trang
   * chủ thì bấm vào là một cú bấm chết. Đi mềm (`<Link>`), không nạp lại: địa
   * chỉ này không đổi server đang chọn, nên `isAdmin` đã tính đúng sẵn cho nó.
   */
  const panelHref = selectedGuildId() === '' ? '/' : '/overview';

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-border bg-surface/90 backdrop-blur">
        <div className={`${container} flex items-center justify-between gap-3 py-3`}>
          <Link to="/" className="text-lg font-bold tracking-tight whitespace-nowrap">
            {landing.brand}
          </Link>

          <nav className="flex items-center gap-0.5 overflow-x-auto sm:gap-1">
            {landing.nav.map((item) => {
              const active = location.pathname === item.url;
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={
                    active
                      ? 'rounded-lg bg-surface-3 px-3 py-1.5 text-sm font-medium whitespace-nowrap'
                      : 'rounded-lg px-3 py-1.5 text-sm whitespace-nowrap text-fg-muted hover:bg-surface-3'
                  }
                >
                  {item.label}
                </Link>
              );
            })}
            {/*
              Đã đăng nhập thì đây là lối VÀO PANEL, không phải nút đăng nhập nữa.

              Trang công khai không đòi phiên, nên trước đây nó vẽ y hệt nhau cho
              người đã đăng nhập và người chưa — và cái nút "Đăng nhập" còn nguyên
              ở góc là bằng chứng duy nhất người dùng có trên màn hình. Với người
              vừa đăng nhập xong, bằng chứng đó nói sai.

              `loading` là nhịp đầu, khi `/api/auth/me` chưa trả lời. Ô giữ chỗ
              mang ĐÚNG nhãn dài nhất nhưng `invisible`, nên nó chiếm sẵn đúng bề
              ngang cần thiết: không nhảy layout khi câu trả lời về, và cũng không
              hiện chữ "Đăng nhập" trong một nhịp cho người đã đăng nhập — nhấp
              nháy đúng cái nhãn sai là cách chắc nhất để dựng lại hiểu lầm mà cả
              lượt sửa này đi giải quyết.
            */}
            {loading ? (
              <span
                aria-hidden
                className="invisible ml-1 rounded-lg px-3.5 py-1.5 text-sm font-medium whitespace-nowrap"
              >
                {landing.buttons.login.label}
              </span>
            ) : user !== null ? (
              <Link
                to={panelHref}
                className="ml-1 rounded-lg bg-accent px-3.5 py-1.5 text-sm font-medium whitespace-nowrap text-white hover:bg-accent-hover"
              >
                {landing.buttons.openPanel.label}
              </Link>
            ) : (
              <a
                href={loginHref(location.pathname)}
                className="ml-1 rounded-lg bg-accent px-3.5 py-1.5 text-sm font-medium whitespace-nowrap text-white hover:bg-accent-hover"
              >
                {landing.buttons.login.label}
              </a>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border">
        <div
          className={`${container} flex flex-col items-center justify-between gap-3 py-6 text-sm text-fg-subtle sm:flex-row`}
        >
          <span>{landing.footer}</span>
          <span className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <a
              href={landing.buttons.addBot.url}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {landing.buttons.addBot.label}
            </a>
            <Link to={landing.buttons.terms.url} className="hover:underline">
              {landing.buttons.terms.label}
            </Link>
            <Link to={landing.buttons.privacy.url} className="hover:underline">
              {landing.buttons.privacy.label}
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

/** Tiêu đề trang public — một kiểu chữ cho cả bốn trang, khai một lần. */
export function PublicHeading({
  title,
  intro,
}: {
  title: string;
  intro?: string;
}): React.JSX.Element {
  return (
    <div className="pt-10 pb-6">
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
      {intro !== undefined && (
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-fg-muted">
          {intro}
        </p>
      )}
    </div>
  );
}
